import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Clock, CreditCard, FileText, Users, Gauge, PenTool,
  Layers, Grid3x3, Newspaper, BookOpen, Activity, Trophy, Quote,
  Brain, Target, BarChart3, AlertTriangle, RefreshCw, Video, EyeOff,
  ClipboardList, HelpCircle, BadgeCheck,
} from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

/* Premium light "Intelligence Layer" sections.
   Light alternating backgrounds · white floating cards · CLARIAS Blue accent.
   Product panels stay dark for premium contrast (the software is the hero). */
const DISPLAY = "font-['Outfit']";
const WHITE = "#FFFFFF", GRAY = "#F5F6F8", BLUE = "#2563EB", PANEL = "#0B1020";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);
const Section = ({ id, bg = WHITE, children }) => (
  <section id={id} style={{ backgroundColor: bg }} className="w-full font-['Inter'] text-[#0B1020]">
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-24 md:py-32">{children}</div>
  </section>
);
const Overline = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563EB] mb-4">{children}</p>
);
const H2 = `${DISPLAY} text-3xl md:text-[40px] leading-[1.12] tracking-tight font-semibold text-[#0B1020]`;
const cardCls = "rounded-2xl bg-white border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_14px_36px_-20px_rgba(0,0,0,0.20)]";

/* ── Dark product panels (kept dark = the software, for contrast) ────── */
const Bar = ({ label, value, tone }) => {
  const t = tone || (value >= 60 ? "#22c55e" : value >= 40 ? "#f59e0b" : "#ef4444");
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="flex justify-between text-[11px] text-slate-400 mb-1"><span>{label}</span><span>{value}%</span></div>
      <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: t }} initial={{ width: 0 }}
          whileInView={{ width: `${value}%` }} viewport={{ once: true }} transition={{ duration: 1, ease: "easeOut" }} />
      </div>
    </div>
  );
};
const Ring = ({ value, label }) => {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="relative w-[92px] h-[92px]">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
        <motion.circle cx="40" cy="40" r={r} fill="none" stroke={BLUE} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} whileInView={{ strokeDashoffset: c - (c * value) / 100 }}
          viewport={{ once: true }} transition={{ duration: 1.2, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${DISPLAY} text-2xl font-semibold text-white`}>{value}</span>
        <span className="text-[9px] uppercase tracking-wide text-slate-500">{label}</span>
      </div>
    </div>
  );
};
const Panel = ({ children }) => (
  <div className="rounded-2xl border border-white/[0.08] bg-[#0B1020] p-5 md:p-6 h-full shadow-[0_20px_50px_-25px_rgba(0,0,0,0.6)]">{children}</div>
);
export const DashPanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-4"><Gauge className="w-4 h-4 text-[#3b82f6]" /> Decision Dashboard</div>
    <div className="flex items-center gap-4">
      <Ring value={72} label="Ready" />
      <div className="flex-1 space-y-2"><Bar label="Polity" value={38} tone="#ef4444" /><Bar label="Economy" value={52} tone="#f59e0b" /><Bar label="Ethics" value={64} tone="#22c55e" /></div>
    </div>
  </Panel>
);
export const AnswerPanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-4"><PenTool className="w-4 h-4 text-[#3b82f6]" /> Topper Answer Generator</div>
    <div className="flex gap-1.5 mb-3 flex-wrap">{["GS1", "GS2", "GS3", "GS4", "Essay"].map((p, i) => <span key={p} className={`text-[10px] px-2 py-1 rounded-md ${i === 1 ? "bg-[#2563EB] text-white" : "bg-white/[0.06] text-slate-400"}`}>{p}</span>)}</div>
    <div className="rounded-lg bg-white/[0.04] p-2.5 text-[11px] text-slate-400 mb-2">"Examine the role of cooperative federalism in Indian polity."</div>
    <div className="space-y-1.5">{[["Introduction", "Context + definition"], ["Body", "Multi-dimensional + examples"], ["Value Addition", "Committees · Articles · Data"], ["Conclusion", "Forward-looking"]].map(([t, d]) => (
      <div key={t} className="flex items-center gap-2 text-[11px]"><Check className="w-3 h-3 text-emerald-400 shrink-0" /><span className="text-slate-200 font-medium">{t}</span><span className="text-slate-500 truncate">— {d}</span></div>))}</div>
  </Panel>
);
const PYQPanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-4"><Layers className="w-4 h-4 text-[#3b82f6]" /> PYQ Analyzer</div>
    <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Most-asked topics · 2014–2024</p>
    <Bar label="Fundamental Rights" value={88} tone={BLUE} /><Bar label="Indian Economy — Growth" value={74} tone={BLUE} /><Bar label="Environment — Conventions" value={61} tone={BLUE} /><Bar label="Modern History — Movements" value={49} tone={BLUE} />
  </Panel>
);
const HeatmapPanel = () => {
  const cells = [90, 20, 60, 80, 35, 15, 70, 45, 95, 30, 55, 25, 85, 40, 65, 10, 75, 50, 20, 88, 33, 60, 42, 78, 15, 92, 48, 28];
  return (
    <Panel>
      <div className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-4"><Grid3x3 className="w-4 h-4 text-[#3b82f6]" /> Topic Heatmap</div>
      <div className="grid grid-cols-7 gap-1.5">{cells.map((v, i) => <motion.div key={i} className="aspect-square rounded-[3px]" style={{ backgroundColor: `rgba(37,99,235,${(v / 100) * 0.9 + 0.06})` }} initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.012 }} />)}</div>
      <p className="text-[10px] text-slate-500 mt-3">Darker = higher UPSC weightage</p>
    </Panel>
  );
};
const CAPanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-4"><Newspaper className="w-4 h-4 text-[#3b82f6]" /> Current Affairs Intelligence</div>
    <div className="space-y-2">{[["Digital Personal Data Protection", "GS2 · Governance", "High"], ["India's Semiconductor Push", "GS3 · Economy", "High"], ["Cooperative Federalism Debate", "GS2 · Polity", "Med"]].map(([t, tag, p]) => (
      <div key={t} className="rounded-lg bg-white/[0.04] p-2.5"><p className="text-[11px] text-slate-200 font-medium leading-snug">{t}</p><div className="flex items-center justify-between mt-1"><span className="text-[10px] text-slate-500">{tag}</span><span className={`text-[9px] px-1.5 py-0.5 rounded ${p === "High" ? "bg-[#2563EB]/20 text-[#60a5fa]" : "bg-white/[0.06] text-slate-400"}`}>{p} yield</span></div></div>))}</div>
  </Panel>
);
const PracticePanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-4"><BookOpen className="w-4 h-4 text-[#3b82f6]" /> Practice Hub</div>
    <div className="space-y-2.5">{[["Polity — Union & Territory", 82], ["History — Modern India", 64], ["Geography — Climatology", 45]].map(([t, v]) => (
      <div key={t}><div className="flex justify-between text-[11px] mb-1"><span className="text-slate-200">{t}</span><span className="text-slate-500">{v}%</span></div><Bar label="" value={v} tone={BLUE} /></div>))}</div>
  </Panel>
);
const PerfPanel = () => {
  const pts = [30, 42, 38, 55, 60, 72, 68, 80], w = 220, h = 60;
  const path = pts.map((p, i) => `${(i / (pts.length - 1)) * w},${h - (p / 100) * h}`).join(" ");
  return (
    <Panel>
      <div className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-4"><Activity className="w-4 h-4 text-[#3b82f6]" /> Performance Dashboard</div>
      <div className="flex gap-4 mb-3"><div><p className="text-[10px] uppercase text-slate-500">Percentile</p><p className={`${DISPLAY} text-xl text-white font-semibold`}>91</p></div><div><p className="text-[10px] uppercase text-slate-500">Trend</p><p className="text-xl text-emerald-400 font-semibold">↑</p></div></div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16"><polyline points={path} fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </Panel>
  );
};

const btnPrimary = "inline-flex items-center justify-center gap-2 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3.5 text-sm font-semibold transition-colors shadow-sm shadow-blue-600/20";

/* Scrollable product strip for the DARK hero right column */
export function HeroPanelStrip() {
  const panels = [DashPanel, AnswerPanel, PYQPanel, HeatmapPanel, CAPanel, PracticePanel, PerfPanel];
  return (
    <div className="relative" data-testid="hero-panel-strip">
      <div className="pointer-events-none absolute right-0 top-0 bottom-3 w-14 bg-gradient-to-l from-[#0B1220] to-transparent z-10" />
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-x-light pb-4 pr-6">
        {panels.map((P, i) => (
          <div key={i} className="w-[300px] shrink-0 snap-center rounded-2xl ring-1 ring-white/10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
            <P />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReportPreviewSection() {
  const navigate = useNavigate();
  const startDiagnostic = () => navigate("/free-diagnostic");
  return (
    <Section id="report-preview" bg={GRAY}>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="max-w-xl">
            <Overline>Start here · 5 minutes</Overline>
            <h2 className={H2}>Your free diagnostic → your readiness report</h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">Answer a short set of questions. In under five minutes you'll get a personalized readiness report — your score, your weakest area, and exactly what to fix next. No credit card. No commitment.</p>
            <div className="mt-6 flex flex-wrap gap-2">{["First-timers", "Repeat aspirants", "Working professionals", "Optional aspirants"].map((p) => (
              <span key={p} className="text-xs px-3 py-1.5 rounded-full border border-black/[0.06] bg-white text-slate-600 flex items-center gap-1.5"><Users className="w-3 h-3 text-[#2563EB]" /> {p}</span>))}</div>
            <button onClick={startDiagnostic} className={`${btnPrimary} mt-8`} data-testid="report-diagnostic-btn">Get my readiness report <ArrowRight className="w-4 h-4" /></button>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> No credit card</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Under 5 minutes</span>
              <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Instant report</span>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-white/[0.06] bg-[#0B1020] p-6 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between mb-5"><div className="flex items-center gap-2 text-slate-200 text-sm font-medium"><FileText className="w-4 h-4 text-[#3b82f6]" /> Readiness Report</div><span className="text-[10px] text-slate-500">Sample</span></div>
            <div className="flex items-center gap-5 mb-5"><Ring value={58} label="GS Score" /><div className="flex-1"><p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Verdict</p><p className="text-sm text-slate-200 leading-snug">Borderline — <span className="text-[#60a5fa]">Modern History</span> is pulling your score down.</p></div></div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Subject-wise accuracy</p>
            <Bar label="Polity" value={72} tone={BLUE} /><Bar label="Economy" value={55} tone={BLUE} /><Bar label="Modern History" value={31} tone="#ef4444" />
            <div className="mt-4 pt-4 border-t border-white/[0.08] flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><p className="text-sm text-slate-200">Next step: strengthen Modern History basics before the next test.</p></div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* ── SECTION 2 · Why Aspirants Fail ─────────────────────────────────── */
export function WhyFailSection() {
  const cards = [
    { icon: PenTool, title: "Wrong Answer Structure", desc: "Strong content, weak structure — marks quietly slip away in Mains.", bg: "bg-rose-50", br: "border-rose-100", tx: "text-rose-600" },
    { icon: RefreshCw, title: "Poor Revision Decisions", desc: "Revising everything, mastering nothing before the exam arrives.", bg: "bg-amber-50", br: "border-amber-100", tx: "text-amber-600" },
    { icon: AlertTriangle, title: "Over Attempting", desc: "Guessing under pressure turns Prelims into an avoidable gamble.", bg: "bg-orange-50", br: "border-orange-100", tx: "text-orange-600" },
    { icon: Layers, title: "Weak PYQ Understanding", desc: "Studying blind to what UPSC actually asks, year after year.", bg: "bg-sky-50", br: "border-sky-100", tx: "text-sky-600" },
    { icon: EyeOff, title: "No Performance Visibility", desc: "No clear view of where marks are being lost until it's too late.", bg: "bg-violet-50", br: "border-violet-100", tx: "text-violet-600" },
  ];
  return (
    <Section id="why-fail" bg={GRAY}>
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Overline>The real problem</Overline>
          <h2 className={H2}>Why aspirants fail UPSC</h2>
        </div>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c, i) => {
          const Ic = c.icon;
          return (
            <Reveal key={c.title} delay={i * 0.06}>
              <div className={`${cardCls} p-7 h-full`} data-testid={`why-fail-card-${i}`}>
                <div className={`w-11 h-11 rounded-xl ${c.bg} border ${c.br} flex items-center justify-center mb-4`}>
                  <Ic className={`w-5 h-5 ${c.tx}`} />
                </div>
                <h3 className={`${DISPLAY} text-lg font-semibold text-[#0B1020] mb-2`}>{c.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.2}>
        <p className="text-center text-xl md:text-2xl text-[#0B1020] font-medium mt-16 max-w-2xl mx-auto leading-snug">
          Preparing harder doesn't always improve results.{" "}
          <span className="text-[#2563EB]">Making better decisions does.</span>
        </p>
      </Reveal>
    </Section>
  );
}

/* ── SECTION 3 · Why Current Solutions Aren't Enough ────────────────── */
export function WhySolutionsSection() {
  const items = [
    { icon: FileText, label: "More Notes", tx: "text-blue-500" },
    { icon: Video, label: "More Videos", tx: "text-rose-500" },
    { icon: BookOpen, label: "More PDFs", tx: "text-amber-500" },
    { icon: ClipboardList, label: "More Tests", tx: "text-emerald-500" },
  ];
  return (
    <Section id="why-solutions" bg={GRAY}>
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Overline>The gap</Overline>
          <h2 className={H2}>Why current solutions aren't enough</h2>
          <p className="mt-5 text-slate-600 text-lg">More material doesn't automatically create better preparation decisions.</p>
        </div>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
        {items.map((it, i) => {
          const Ic = it.icon;
          return (
            <Reveal key={it.label} delay={i * 0.07}>
              <div className={`${cardCls} p-6 text-center h-full flex flex-col items-center justify-center`} data-testid={`solution-gap-${i}`}>
                <Ic className={`w-7 h-7 ${it.tx} mb-3`} />
                <p className="text-[#0B1020] font-medium">{it.label}</p>
                <p className="text-[11px] text-slate-400 mt-2">≠ better decisions</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.25}>
        <div className="mt-16 flex items-center justify-center gap-3 text-center">
          <HelpCircle className="w-6 h-6 text-[#2563EB]" />
          <p className="text-xl md:text-2xl text-[#0B1020] font-medium">So what actually helps?</p>
        </div>
      </Reveal>
    </Section>
  );
}

/* ── SECTION 4 · Why CLARIAS Exists ─────────────────────────────────── */
export function WhyClariasSection() {
  return (
    <Section id="why-clarias-exists" bg={WHITE}>
      <Reveal>
        <div className="max-w-3xl mx-auto text-center">
          <Overline>Why we exist</Overline>
          <p className={`${DISPLAY} text-2xl md:text-4xl font-semibold text-[#0B1020] leading-[1.25] tracking-tight`}>
            Thousands study the same syllabus.{" "}
            <span className="text-slate-400">Only a few succeed.</span>
          </p>
          <p className="mt-6 text-slate-600 text-lg md:text-xl leading-relaxed">
            The difference is rarely effort. It is preparation decisions.
          </p>
          <p className="mt-3 text-[#2563EB] text-lg md:text-xl font-semibold">
            CLARIAS was built to make those decisions visible.
          </p>
        </div>
      </Reveal>
    </Section>
  );
}

/* ── SECTION 5 · Why CLARIAS Is Different ───────────────────────────── */
export function IntelligenceLayerSection() {
  const engines = [
    { icon: Brain, title: "Topper Answer Intelligence", desc: "Separate GS1, GS2, GS3, GS4 & Essay intelligence engines.", bg: "bg-blue-50", br: "border-blue-100", tx: "text-blue-600" },
    { icon: Gauge, title: "Decision Intelligence", desc: "Find where you're losing marks before the exam.", bg: "bg-violet-50", br: "border-violet-100", tx: "text-violet-600" },
    { icon: BarChart3, title: "PYQ Intelligence", desc: "Understand what UPSC repeatedly rewards.", bg: "bg-emerald-50", br: "border-emerald-100", tx: "text-emerald-600" },
  ];
  const seeInAction = () => document.getElementById("see-in-action")?.scrollIntoView({ behavior: "smooth" });
  return (
    <Section id="intelligence-layer" bg={WHITE}>
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Overline>The moat</Overline>
          <h2 className={H2}>Why CLARIAS is different</h2>
          <p className="mt-5 text-slate-600 text-lg">
            Most platforms provide content. <span className="text-[#0B1020] font-medium">CLARIAS provides intelligence.</span>
          </p>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {engines.map((e, i) => {
          const Ic = e.icon;
          return (
            <Reveal key={e.title} delay={i * 0.08}>
              <div className={`${cardCls} p-8 h-full text-center`} data-testid={`intelligence-engine-${i}`}>
                <div className={`w-14 h-14 rounded-2xl ${e.bg} border ${e.br} flex items-center justify-center mx-auto mb-5`}>
                  <Ic className={`w-7 h-7 ${e.tx}`} />
                </div>
                <h3 className={`${DISPLAY} text-xl font-semibold text-[#0B1020] leading-tight mb-3`}>{e.title}</h3>
                <p className="text-slate-600 leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-12 text-center">
          <button onClick={seeInAction} className={btnPrimary} data-testid="see-in-action-btn">
            See CLARIAS in action <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </Reveal>
    </Section>
  );
}

export function JourneySection() {
  return (
    <Section id="journey" bg={WHITE}>
      <Reveal><div className="text-center max-w-2xl mx-auto mb-14"><Overline>The path</Overline><h2 className={H2}>From diagnostic to selection</h2></div></Reveal>
      <div className="flex flex-wrap justify-center gap-3">
        {["Diagnostic", "Personal Strategy", "Foundation", "Current Affairs", "Practice", "Performance Tracking", "Weakness Detection", "Improvement", "Selection"].map((step, i, arr) => (
          <div key={step} className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border ${i === arr.length - 1 ? "border-[#2563EB]/30 bg-blue-50" : "border-black/[0.06] bg-white shadow-sm"}`}>
              {i === arr.length - 1 && <Trophy className="w-4 h-4 text-[#2563EB]" />}
              <span className={`text-sm ${i === arr.length - 1 ? "text-[#2563EB] font-semibold" : "text-slate-700"}`}>{step}</span>
            </div>
            {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 hidden md:block" />}
          </div>
        ))}
      </div>
    </Section>
  );
}

export function TestimonialsSection() {
  // Verified success story — update photo & hall ticket once provided.
  const kedar = {
    name: "Kedar Reddy",
    achievement: "Cleared UPSC Prelims 2026",
    photo: "/kedar-reddy.jpg", // upload later; falls back to initial
    hallTicket: "", // add hall ticket / roll number later for verification
    quote:
      "CLARIAS showed me exactly why I was losing marks in Prelims and which topics actually repeat. I stopped over-studying low-yield areas and focused on decisions that mattered — and it worked.",
  };
  const [photoOk, setPhotoOk] = useState(true);
  return (
    <Section id="voices" bg={WHITE}>
      <Reveal><div className="text-center max-w-2xl mx-auto mb-14"><Overline>Built for serious aspirants</Overline><h2 className={H2}>Why aspirants trust CLARIAS</h2></div></Reveal>

      {/* Featured verified success story */}
      <Reveal>
        <div
          className="relative max-w-4xl mx-auto mb-12 rounded-3xl border border-[#2563EB]/20 bg-gradient-to-br from-blue-50/70 to-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_28px_60px_-28px_rgba(37,99,235,0.35)] p-7 md:p-10"
          data-testid="featured-testimonial-kedar"
        >
          <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold px-3 py-1.5 rounded-full text-white bg-[#2563EB] shadow-sm">
            <BadgeCheck className="w-3.5 h-3.5" /> Verified · Prelims 2026
          </span>
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8">
            <div className="flex-shrink-0">
              {photoOk && kedar.photo ? (
                <img
                  src={kedar.photo}
                  alt={kedar.name}
                  onError={() => setPhotoOk(false)}
                  className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover border border-[#2563EB]/20 shadow-md"
                  data-testid="kedar-photo"
                />
              ) : (
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-gradient-to-br from-[#2563EB] to-blue-700 flex items-center justify-center text-white font-serif font-bold text-4xl shadow-md" data-testid="kedar-photo-fallback">
                  {kedar.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="flex-1 text-center md:text-left">
              <Quote className="w-6 h-6 text-[#2563EB]/50 mx-auto md:mx-0 mb-3" />
              <p className="text-[#0B1020] text-lg md:text-xl leading-relaxed font-medium">
                "{kedar.quote}"
              </p>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 justify-center md:justify-start">
                <div>
                  <p className="font-serif font-bold text-lg text-[#0B1020]" data-testid="kedar-name">{kedar.name}</p>
                  <p className="text-sm text-[#2563EB] font-semibold inline-flex items-center gap-1.5">
                    <Trophy className="w-4 h-4" /> {kedar.achievement}
                  </p>
                </div>
                {kedar.hallTicket && (
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1.5 sm:border-l sm:border-black/10 sm:pl-4">
                    <BadgeCheck className="w-3.5 h-3.5 text-emerald-500" /> Roll No. {kedar.hallTicket}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6">
        {[["The feedback finally told me why I was losing marks, not just that I was.", "Srinu", "Repeat aspirant"], ["The PYQ heatmap changed how I prioritised. I stopped over-studying low-yield topics.", "Ravi", "Working professional"], ["It feels like a strategy layer on top of everything else I already do.", "Swathi", "First-timer"]].map(([q, name, role], i) => (
          <Reveal key={i} delay={i * 0.07}><div className={`${cardCls} p-7 h-full`} data-testid={`testimonial-card-${i}`}><p className="text-slate-700 leading-relaxed mb-6">"{q}"</p><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-[#2563EB] font-semibold text-sm">{name.charAt(0)}</div><div className="leading-tight"><p className="text-sm font-semibold text-[#0B1020]">{name}</p><p className="text-xs text-slate-500">{role}</p></div></div></div></Reveal>))}
      </div>
      <div className="mt-10 overflow-hidden relative"><div className="flex gap-3 w-max animate-marquee">{[...Array(2)].flatMap((_, dup) => ["Finally understood WHY I lost marks.", "The heatmap fixed my prioritisation.", "Feels like a strategy layer on everything.", "Stopped over-studying low-yield topics.", "My answer structure improved in weeks.", "Revision decisions are no longer guesswork."].map((q, i) => (
        <span key={`${dup}-${i}`} className="shrink-0 text-sm text-slate-600 px-4 py-2.5 rounded-full border border-black/[0.06] bg-white flex items-center gap-2"><Quote className="w-3.5 h-3.5 text-[#2563EB]/60" /> {q}</span>)))}</div></div>
    </Section>
  );
}

export function FaqSection() {
  return (
    <Section id="faq" bg={GRAY}>
      <Reveal><div className="text-center max-w-2xl mx-auto mb-12"><Overline>Questions</Overline><h2 className={H2}>Frequently asked</h2></div></Reveal>
      <div className="max-w-3xl mx-auto">
        <Accordion type="single" collapsible>
          {[
            ["Is CLARIAS another coaching platform?", "No. CLARIAS is a decision-intelligence layer that shows you why marks are lost and what to do next, rather than serving more content."],
            ["Do I need to leave my current coaching?", "Not at all. CLARIAS complements any coaching or self-study by adding strategy, diagnostics and pattern intelligence you don't get elsewhere."],
            ["What is the Free Diagnostic?", "A quick 5-minute assessment that returns your readiness score, weakest area and a tailored next step — no signup needed to see your result."],
            ["Which papers does the answer engine support?", "GS1, GS2, GS3, GS4 and Essay — each with its own engine, because different papers reward different thinking."],
            ["How does PYQ Intelligence help?", "It reads a decade of past papers as signal — heatmaps, frequency and trends — so you study what UPSC repeatedly rewards."],
            ["Is my data private?", "Yes. Your performance data is used only to power your own insights and recommendations."],
            ["Is there a free plan?", "Yes — start free with the diagnostic and core intelligence, and upgrade only when the value is obvious."],
            ["Does it work on mobile?", "Fully. CLARIAS is built mobile-first so you can practise and review anywhere."],
          ].map(([q, a], i) => (
            <AccordionItem key={i} value={`f${i}`} className="border-black/[0.08]">
              <AccordionTrigger data-testid={`faq-trigger-${i + 1}`} className="text-[#0B1020] hover:no-underline text-left py-5">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
}
